import produce from "immer";
import {
  GENERATIVE_STARTED,
  GENERATIVE_PROGRESS,
  GENERATIVE_ALL_COMPLETED,
  GENERATIVE_ALL_FAILED,
  GENERATIVE_JOB_DISMISS,
} from "../types";

/**
 * Generative Scheduling progress slice.
 * Shape: { jobs: { [jobId]: JobState } }
 * Each generation run (per scenarioId) is one job.
 * totalProjects = 1 (a generation is always a single run).
 */
const initialJobState = {
  active: false,
  open: false,
  startedAt: null,
  terminatedAt: null,
  totalProjects: 1,
  completedCount: 0,
  failedCount: 0,
  etaSeconds: null,
  error: null,
  scenarioId: null,
  scenarioName: null,
  operation: null,
};

export const initialState = { jobs: {} };

export default function generativeSchedulingReducer(state = initialState, action) {
  const { type, payload } = action;
  switch (type) {
    case GENERATIVE_STARTED:
      return produce(state, (draft) => {
        // Merge onto an existing job so a re-dispatched STARTED (rehydration)
        // updates the provided fields without zeroing the rest. Bespoke shape
        // (no ...payload), so apply each field only when present.
        const prev = draft.jobs[payload.jobId];
        draft.jobs[payload.jobId] = {
          ...initialJobState,
          ...(prev ?? {}),
          active: true,
          open: true,
          startedAt: prev?.startedAt ?? payload.startedAt ?? 0,
          scenarioId: payload.scenarioId ?? prev?.scenarioId ?? null,
          scenarioName: payload.scenarioName ?? prev?.scenarioName ?? null,
          operation: payload.operation ?? prev?.operation ?? null,
          etaSeconds: payload.etaSeconds ?? prev?.etaSeconds ?? null,
          jobId: payload.jobId,
        };
      });

    case GENERATIVE_PROGRESS:
      return produce(state, (draft) => {
        const job = draft.jobs[payload.jobId];
        if (!job) return;
        const eta =
          payload?.etaSecondsRemaining ??
          payload?.estimatedRemainingSeconds ??
          payload?.etaSeconds ??
          null;
        if (eta != null) job.etaSeconds = eta;
      });

    case GENERATIVE_ALL_COMPLETED:
      return produce(state, (draft) => {
        const job = draft.jobs[payload?.jobId];
        if (!job) return;
        job.active = false;
        job.completedCount = 1;
        job.etaSeconds = 0;
        job.terminatedAt = payload?.terminatedAt ?? Date.now();
      });

    case GENERATIVE_ALL_FAILED:
      return produce(state, (draft) => {
        const job = draft.jobs[payload?.jobId];
        if (!job) return;
        job.active = false;
        job.failedCount = 1;
        job.error = payload?.error || "Generation failed";
        job.terminatedAt = payload?.terminatedAt ?? Date.now();
      });

    case GENERATIVE_JOB_DISMISS:
      return produce(state, (draft) => {
        if (payload?.jobId === "all") {
          Object.keys(draft.jobs).forEach((id) => {
            if (!draft.jobs[id].active) delete draft.jobs[id];
          });
        } else if (payload?.jobId) {
          delete draft.jobs[payload.jobId];
        }
      });

    default:
      return state;
  }
}
